import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner, Table } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import { fmtAmount } from "./format";

interface Props {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  details: any;
  /** At least one invoice already generated — blocks plan reconcile (Java parity). */
  invoiceGenerated: boolean;
  onSuccess: () => void;
}

/**
 * Reconcile modal — ports the legacy Java flow
 * (EnrollmentViewWithSuggestedBlocks2.jsp #handleManualReconcile):
 * pick "For Plan", choose an installment template, preview the resulting plan,
 * then rebuild. "For Module" (program-based) is shown disabled — the module
 * rebuild is a separate phase. Plan reconcile is blocked once an invoice exists.
 */
const ReconcileModal: React.FC<Props> = ({
  show,
  onHide,
  enrollmentId,
  details,
  invoiceGenerated,
  onSuccess,
}) => {
  const isProgramBased = !!details?.isProgramBased;

  const [forModule, setForModule] = useState(false);
  const [forPlan, setForPlan] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(null);

  const [preview, setPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleClose = () => {
    if (successMsg) onSuccess();
    else onHide();
  };

  // Load eligible templates when the modal opens.
  useEffect(() => {
    if (!show) return;
    setForModule(false);
    setForPlan(false);
    setTemplateId(null);
    setPreview(null);
    setError(null);
    setConfirming(false);
    setSuccessMsg(null);

    const filter = isProgramBased
      ? {
          programId: details?.programId,
          yearId: details?.batchYearId || details?.entryYearId,
          schoolId: details?.schoolId,
          batchId: details?.batchId,
        }
      : {
          courseId: details?.courseId,
          yearId: details?.batchYearId || details?.entryYearId,
          schoolId: details?.schoolId,
          batchId: details?.batchId,
        };

    setTemplatesLoading(true);
    EnrollmentService.getReconcileTemplates(filter)
      .then((res: any) => {
        if (res.data?.success)
          setTemplates(res.data.data.installmentTemplates || []);
        else setError(res.data?.message || "Failed to load templates");
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message || err.message || "Failed to load templates",
        ),
      )
      .finally(() => setTemplatesLoading(false));
  }, [show, isProgramBased, details, enrollmentId]);

  const loadPreview = (tid: number) => {
    setPreview(null);
    if (!tid) return;
    setPreviewLoading(true);
    setError(null);
    EnrollmentService.getPreplan(enrollmentId, tid)
      .then((res: any) => {
        if (res.data?.success) setPreview(res.data.data);
        else setError(res.data?.message || "Failed to build preview");
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message || err.message || "Failed to build preview",
        ),
      )
      .finally(() => setPreviewLoading(false));
  };

  // 0 = module + plan, 1 = module only, 2 = plan only.
  const typeId = forModule && forPlan ? 0 : forModule ? 1 : forPlan ? 2 : -1;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await EnrollmentService.reconcilePlan(enrollmentId, {
        typeId,
        installmentTemplateId: templateId,
      });
      if (res.data?.success) {
        setSuccessMsg(res.data.message || "Plan reconciled successfully.");
      } else {
        setError(res.data?.message || "Failed to reconcile plan");
        setConfirming(false);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Failed to reconcile plan",
      );
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTemplateName =
    templates.find((t) => t.id === templateId)?.name || "the selected template";

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered contentClassName="ev-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Reconcile</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && (
          <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>
        )}

        {!successMsg && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                padding: "12px 14px",
                background: "var(--surface-page)",
                border: "1px solid var(--border-default)",
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-muted)" }}>
                What to reconcile
              </div>
              {isProgramBased && (
                <Form.Check
                  type="checkbox"
                  id="reconcileForModule"
                  checked={forModule}
                  onChange={(e) => setForModule(e.target.checked)}
                  label="For Module"
                />
              )}

              <Form.Check
                type="checkbox"
                id="reconcileForPlan"
                checked={forPlan}
                disabled={invoiceGenerated}
                onChange={(e) => {
                  setForPlan(e.target.checked);
                  if (!e.target.checked) {
                    setTemplateId(null);
                    setPreview(null);
                  }
                }}
                label={
                  <span>
                    For Plan{" "}
                    {invoiceGenerated && (
                      <span style={{ color: "var(--status-danger)", fontSize: 11, fontWeight: 500 }}>
                        — invoice(s) generated, plan reconcile disabled
                      </span>
                    )}
                  </span>
                }
              />
            </div>

            {forPlan && (
              <div style={{ marginTop: 14 }}>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                  Installment Template
                </Form.Label>
                {templatesLoading ? (
                  <div className="ev-loading" style={{ justifyContent: "flex-start" }}>
                    <Spinner role="status" animation="border" size="sm" /> Loading templates...
                  </div>
                ) : (
                  <Form.Select
                    size="sm"
                    value={templateId || ""}
                    onChange={(e) => {
                      const tid = Number(e.target.value) || null;
                      setTemplateId(tid);
                      if (tid) loadPreview(tid);
                      else setPreview(null);
                    }}
                    style={{ maxWidth: 380 }}
                  >
                    <option value="">Select a template</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Form.Select>
                )}
              </div>
            )}

            {previewLoading && (
              <div className="ev-loading">
                <Spinner role="status" animation="border" size="sm" /> Building preview...
              </div>
            )}

            {preview && !previewLoading && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-body)", marginBottom: 6 }}>
                  Plan Preview
                </div>
                <div style={{ overflowX: "auto" }}>
                <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Installment</th>
                      {preview.heads.map((h: any) => (
                        <th key={h.id} style={{ textAlign: "right" }}>{h.name}</th>
                      ))}
                      <th style={{ textAlign: "right" }}>Plan Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.installments.map((inst: any) => (
                      <tr key={inst.installmentId}>
                        <td style={{ whiteSpace: "nowrap" }}>{inst.installmentDate}</td>
                        <td>{inst.installmentName}</td>
                        {preview.heads.map((h: any) => (
                          <td key={h.id} style={{ textAlign: "right" }}>
                            {inst.headAmounts[h.id] != null
                              ? fmtAmount(inst.headAmounts[h.id])
                              : "-"}
                          </td>
                        ))}
                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                          {fmtAmount(inst.planAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="ev-plan-total-row">
                      <td colSpan={2 + preview.heads.length}>Total</td>
                      <td style={{ textAlign: "right" }}>
                        {fmtAmount(preview.totalPlanAmount)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
                </div>
              </div>
            )}

            {confirming && (
              <Alert variant="warning" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                {forModule && (
                  <div>
                    This <b>rebuilds all modules</b> for the program/year — existing
                    module state (dropped modules, fees, grades links) is replaced.
                  </div>
                )}
                {forPlan && (
                  <div>
                    This replaces the existing plan with <b>{selectedTemplateName}</b>.
                  </div>
                )}
                <div>This cannot be undone. Continue?</div>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={handleClose}>Close</Button>
        ) : confirming ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <><Spinner role="status" animation="border" size="sm" /> Reconciling...</>
              ) : (
                "Yes, replace plan"
              )}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                (!forModule && !forPlan) ||
                (forPlan && !templateId) ||
                previewLoading
              }
              onClick={() => setConfirming(true)}
            >
              Save
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReconcileModal;
